import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaReact,
  FaNodeJs,
  FaPhp,
  FaGitAlt,
  FaAws,
  FaJava,
  FaPython,
  FaCogs,
} from "react-icons/fa";
import {
  SiJavascript,
  SiTypescript,
  SiMongodb,
  SiHtml5,
  SiCss3,
  SiMysql,
  SiTailwindcss,
  SiRedux,
  SiWebpack,
  SiLess,
  SiFramer,
  SiUml,
  SiAlibabacloud,
  SiWordpress,
  SiPowerbi,
  SiCanva,
} from "react-icons/si";

export default function TeckStacks() {
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });

  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [controls, inView]);

  const skills = [
    {
      category: "Programming Languages",
      items: [
        { name: "Python", icon: FaPython, color: "#306998" },
        { name: "Java", icon: FaJava, color: "#007396" },
        { name: "PHP", icon: FaPhp, color: "#8892bf" },
        { name: "JavaScript", icon: SiJavascript, color: "#F7DF1E" },
        { name: "TypeScript", icon: SiTypescript, color: "#3178C6" },
        { name: "C#", icon: FaCogs, color: "#9B4F96" },
      ],
    },
    {
      category: "Frontend Technologies",
      items: [
        { name: "HTML5", icon: SiHtml5, color: "#E34F26" },
        { name: "CSS", icon: SiCss3, color: "#1572B6" },
        { name: "LESS", icon: SiLess, color: "#1D365D" },
        { name: "React", icon: FaReact, color: "#61DAFB" },
        { name: "WordPress", icon: SiWordpress, color: "#21759B" },
        { name: "Framer Motion", icon: SiFramer, color: "#EC5990" },
        { name: "Tailwind CSS", icon: SiTailwindcss, color: "#38B2AC" },
        { name: "Redux", icon: SiRedux, color: "#764ABC" },
        { name: "Webpack", icon: SiWebpack, color: "#8ED6FB" },
        { name: "Axios", icon: FaCogs, color: "#5A29E4" },
      ],
    },
    {
      category: "Backend Technologies & Databases",
      items: [
        { name: "Node.js", icon: FaNodeJs, color: "#68A063" },
        { name: "MySQL", icon: SiMysql, color: "#00758F" },
        { name: "MongoDB", icon: SiMongodb, color: "#47A248" },
        { name: "PHP", icon: FaPhp, color: "#8892BF" },
        { name: "PowerBI", icon: SiPowerbi, color: "#F2C811" },
      ],
    },
    {
      category: "Cloud Technologies",
      items: [
        { name: "AWS Cloud", icon: FaAws, color: "#FF9900" },
        { name: "Alibaba Cloud", icon: SiAlibabacloud, color: "#FF6A00" },
      ],
    },
    {
      category: "Version Control & CI/CD",
      items: [{ name: "Git", icon: FaGitAlt, color: "#F05032" }],
    },
    {
      category: "Design Tools",
      items: [{ name: "Canva", icon: SiCanva, color: "#00C4CC" }],
    },
    {
      category: "Miscellaneous",
      items: [{ name: "UML", icon: SiUml, color: "#0077B5" }],
    },
  ];

  const containerVariant = {
    hidden: { opacity: 0, y: 100 },
    visible: { opacity: 1, y: 0 },
  };

  const skillVariant = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
  };

  return (
    <div className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            These are the primary technologies I use in my projects
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto pt-2">
            Here's a look at the technologies I'm proficient with.
          </p>
        </div>

        <motion.div
          ref={ref}
          variants={containerVariant}
          initial="hidden"
          animate={controls}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          {skills.map((skillCategory, categoryIndex) => (
            <div key={skillCategory.category} className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 pt-3">
                {skillCategory.category}
              </h3>
              <dl className="mt-4 space-y-10 md:space-y-0 md:grid md:grid-cols-3 md:gap-x-8 md:gap-y-10 pt-2 sm:grid sm:grid-cols-2">
                {skillCategory.items.map((skill, index) => (
                  <motion.div
                    key={skill.name}
                    variants={skillVariant}
                    initial="hidden"
                    animate={controls}
                    transition={{
                      duration: 0.6,
                      delay: 0.15 * (categoryIndex * 3 + index),
                      type: "spring",
                      stiffness: 60,
                      damping: 20,
                    }}
                    className="relative"
                  >
                    <dt className="flex items-center">
                      <div
                        className="flex-shrink-0 h-12 w-12 rounded-md flex items-center justify-center"
                        style={{ backgroundColor: skill.color }}
                      >
                        <skill.icon className="h-6 w-6 text-white" aria-hidden="true" />
                      </div>
                      <p className="ml-4 text-lg leading-6 font-medium text-gray-900 text-left md:text-center">
                        {skill.name}
                      </p>
                    </dt>
                  </motion.div>
                ))}
              </dl>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
